import type { DataTagSummary } from '@robovision/data'
import {
  ImportExportIOJobStatus,
  type ImportExportRecordImportJobResponse,
} from '@robovision/import-export'


type ConstEnum<T, V = string> = Extract<T[keyof T], V>

export const ImportJobStatus = {
  ...ImportExportIOJobStatus,
  UPLOADING: 'UPLOADING',
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ImportJobStatus = ConstEnum<typeof ImportJobStatus>

interface ImportJobParams {
  amountDone?: number
  amountFailed?: number
  amountTotal?: number
  createdAt?: Date
  dataFormat?: string
  datapoolId: string
  error?: string
  importId: string
  jobId?: string
  name: string
  ownerId?: string
  sampleCount?: number
  status?: ImportJobStatus
  tagAttributes?: Record<string, string>
  tagId?: string
}

export class ImportJob {
  abortController?: AbortController
  amountDone?: number
  amountFailed?: number
  amountTotal?: number
  createdAt: Date
  dataFormat?: string
  datapoolId: string
  error?: string
  importId: string // an internal id to keep track of jobs, may or may not be the same as the backend job id
  jobId?: string
  name: string
  ownerId?: string
  sampleCount?: number
  status: ImportJobStatus
  tagId?: string
  tagAttributes?: Record<string, string>
  uploadProgress?: number

  constructor (params: ImportJobParams) {
    this.amountDone = params.amountDone
    this.amountFailed = params.amountFailed
    this.amountTotal = params.amountTotal
    this.createdAt = params.createdAt ?? new Date()
    this.dataFormat = params.dataFormat
    this.datapoolId = params.datapoolId
    this.error = params.error
    this.importId = params.importId
    this.jobId = params.jobId
    this.name = params.name
    this.ownerId = params.ownerId
    this.status = params.status ?? ImportJobStatus.UPLOADING
    this.tagAttributes = params.tagAttributes
    this.sampleCount = params.sampleCount
    this.tagId = params.tagId
  }

  public get amountSuccess () {
    return (this.amountDone ?? 0) - (this.amountFailed ?? 0)
  }

  /**
   * Returns `true` if the import job is either successfully finished or failed
   */
  public get isSettled () {
    return this.isDone ||
      this.isStopped ||
      this.status === ImportJobStatus.FAILED ||
      this.status === ImportJobStatus.INCOMPATIBLE_FORMAT ||
      this.status === ImportJobStatus.KILLED
  }

  public get isDone (): boolean {
    return this.status === ImportJobStatus.DONE
  }

  public get isStopped (): boolean {
    return this.status === ImportJobStatus.STOPPED
  }

  public get progress () {
    if (!this.amountTotal) return 0
    return (this.amountDone ?? 0) / this.amountTotal
  }

  public static fromResponse (
    data: ImportExportRecordImportJobResponse,
    tag: DataTagSummary,
    importId?: string,
  ) {
    return new ImportJob({
      amountDone: data.amountDone,
      amountFailed: data.amountFailed,
      amountTotal: data.amountTotal,
      createdAt: new Date(data.createdAt),
      dataFormat: data.dataFormat,
      datapoolId: data.datapoolId,
      importId: importId ?? data.jobId,
      jobId: data.jobId,
      name: tag.name,
      ownerId: data.tagMetadata?.ownerId,
      sampleCount: data.tagMetadata?.sampleCount,
      status: data.jobStatus,
      tagAttributes: data.tagMetadata?.attributes,
      tagId: data.tagId,
    })
  }
}
